/**
 * 统一菜单配置
 * 用于大厅侧边栏和游戏页面底部按钮
 */

import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores'

/**
 * 菜单项类型
 */
export interface MenuItem {
  /** 菜单ID */
  id: string
  /** 图标路径 (大厅侧边栏使用) */
  icon?: string
  /** 菜单文本 */
  text: string
  /** 是否显示 (可以是 boolean 或 computed) */
  display: boolean | (() => boolean)
}

/**
 * 获取所有菜单项配置
 * 根据用户状态动态显示/隐藏某些菜单项
 */
export function useMenuItems() {
  const { t } = useI18n()
  const userStore = useUserStore()

  // ========== 用户类型判断 ==========

  /**
   * 游客玩家: 不能修改密码
   * userType === '11'
   */
  const isTrialPlayer = computed(() => userStore.userType === '11')

  /**
   * 网投会员: 没有指令、可以投注
   * vipType === '1'
   */
  const isVipPlayer = computed(() => userStore.vipType === '1')

  /**
   * 高级电话会员: 有所有功能
   * vipType === '2'
   */
  const isHighPhoneVipPlayer = computed(() => userStore.vipType === '2')

  /**
   * 电话会员: 竖屏不能投注和指令,横屏可以有指令
   * vipType === '0'
   */
  const isPhoneVipPlayer = computed(() => userStore.vipType === '0')

  // ========== 屏幕方向判断 ==========

  /**
   * 是否横屏
   */
  const isHorizontal = computed(() => {
    return window.innerWidth > window.innerHeight
  })

  // ========== 功能显示控制 ==========

  /**
   * 是否展示指令相关功能
   * - 高级电话会员: 有所有功能
   * - 横屏的电话会员: 有指令
   * - 其他: 无指令
   */
  const displayCommand = computed(() => {
    // 高级电话会员有所有功能
    if (isHighPhoneVipPlayer.value) return true
    // 横屏的电话会员有指令
    if (isPhoneVipPlayer.value && isHorizontal.value) return true
    return false
  })

  /**
   * 是否展示投注相关功能 (配置筹码)
   * - 高级电话会员: 可以投注
   * - 网投会员: 可以投注
   * - 其他: 不能投注
   */
  const displayBet = computed(() => {
    // 高级电话会员有所有功能
    if (isHighPhoneVipPlayer.value) return true
    // 网投会员有投注
    if (isVipPlayer.value) return true
    return false
  })

  /**
   * 是否显示客服按钮
   * - agentType === '0': 不显示客服按钮
   * - agentType === '1': 显示联系方式 (公司电话)
   * - agentType === '2': 打开公司客服 (在线客服)
   */
  const displayCustomer = computed(() => userStore.agentType !== '0')

  /**
   * 所有菜单项配置
   */
  const menuItems = computed<MenuItem[]>(() => [
    // 游戏大厅
    // {
    //   id: 'hall',
    //   icon: new URL('@/assets/images/home/config_11.png', import.meta.url).href,
    //   text: t('menu_hall'),
    //   display: true,
    // },
    // 下注报表
    {
      id: 'report',
      icon: new URL('@/assets/images/home/config_1.svg', import.meta.url).href,
      text: t('menu_report'),
      display: true,
    },
    // 游戏规则
    {
      id: 'rules',
      icon: new URL('@/assets/images/home/config_2.svg', import.meta.url).href,
      text: t('menu_rules'),
      display: true,
    },
    // 配置筹码 - 根据用户类型控制显示
    {
      id: 'custom-bet',
      icon: new URL('@/assets/images/home/config_3.svg', import.meta.url).href,
      text: t('menu_custom_bet'),
      display: displayBet.value,
    },
    // 游戏音乐(背景音乐)
    {
      id: 'music',
      icon: new URL('@/assets/images/home/config_4.svg', import.meta.url).href,
      text: t('menu_music'),
      display: true,
    },
    // 视频线路
    {
      id: 'video-line',
      icon: new URL('@/assets/images/home/config_10.png', import.meta.url).href,
      text: t('menu_video_line'),
      display: true,
    },
    // 开关设置
    {
      id: 'settings',
      icon: new URL('@/assets/images/home/config_5.svg', import.meta.url).href,
      text: t('menu_settings'),
      display: true,
    },
    // 指令说明(操作说明) - 根据用户类型控制显示
    {
      id: 'command',
      icon: new URL('@/assets/images/home/config_6.svg', import.meta.url).href,
      text: t('menu_command'),
      display: displayCommand.value,
    },
    // 修改密码 - 游客不能修改密码
    {
      id: 'change-password',
      icon: new URL('@/assets/images/home/config_7.svg', import.meta.url).href,
      text: t('menu_change_password'),
      display: !isTrialPlayer.value,
    },
    // 联系客服 - 根据客服类型控制显示
    {
      id: 'contact',
      icon: new URL('@/assets/images/home/config_8.svg', import.meta.url).href,
      text: t('menu_contact'),
      display: displayCustomer.value,
    },
  ])

  return {
    menuItems,
    isTrialPlayer,
    displayCommand,
    displayCustomer,
  }
}
